/**
 * Story 049 — shared widget header chrome (drag handle + title + hide-X).
 *
 * Mirrors `apps/lyse/src/features/analysis/AnalysisWorkspace.tsx`'s
 * `WidgetSlot` verbatim per UX brief §3.1. The DOM shape, role,
 * tabIndex, aria-labels, focus ring, header typography, and inner-body
 * `overflow: auto` are all identical so users get one gesture
 * vocabulary across the product.
 *
 * Two intentional deltas vs AnalysisWorkspace:
 *   1. The drag handle's `aria-label` reads "Move {title}" using the
 *      Dashboard widget's title (e.g. "Move Open Inspections") — same
 *      pattern, different titles.
 *   2. Keyboard handling adds `+` / `-` keys for resize (UX brief
 *      §6.2 — "(NEW for 049) Resize +1 cell in width AND height").
 *      AnalysisWorkspace today only nudges; it does not resize from
 *      the keyboard.
 *
 * RGL passes per-cell `style`, `className`, `onMouseDown`,
 * `onMouseUp`, `onTouchEnd` via `React.cloneElement` on the immediate
 * child of `<ResponsiveGridLayout>`. We forward them to the outer
 * `<Paper>` so RGL can drive the absolute-positioning + drag wiring.
 */
import type {
  KeyboardEvent,
  MouseEventHandler,
  TouchEventHandler,
} from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { Layout } from "react-grid-layout";
import {
  DRAG_HANDLE_CLASS,
  type WidgetKey,
} from "../shared/factoryDefaults.js";
import { GripVerticalIcon, XIcon } from "@aeriosiq/ui/icons";

// Match the AnalysisWorkspace radius constant so the visual rhythm is
// pixel-identical between the Dashboard and the analysis canvas. UX
// brief §3.1 — "borderRadius: ANALYSIS_PANEL_RADIUS (already 12 px)".
// AnalysisWorkspace exports the constant as 10; we copy the value
// rather than reaching across module boundaries — Dashboard lives in
// apps/web, the analysis surface lives in apps/lyse, and we don't want
// the umbrella shell importing from a feature module.
const PANEL_RADIUS_PX = 10;

export interface WidgetSlotProps {
  widgetKey: WidgetKey;
  title: string;
  onHide: (key: WidgetKey) => void;
  onKeyboardNudge: (
    key: WidgetKey,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ) => void;
  layoutEntry: Layout | undefined;
  children: React.ReactNode;
  /** Forwarded by RGL via React.cloneElement on the grid item child. */
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: MouseEventHandler;
  onMouseUp?: MouseEventHandler;
  onTouchEnd?: TouchEventHandler;
}

export function WidgetSlot({
  widgetKey,
  title,
  onHide,
  onKeyboardNudge,
  layoutEntry,
  children,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
}: WidgetSlotProps): React.ReactElement {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    let dx = 0;
    let dy = 0;
    let dw = 0;
    let dh = 0;
    switch (event.key) {
      case "ArrowLeft":
        dx = -1;
        break;
      case "ArrowRight":
        dx = 1;
        break;
      case "ArrowUp":
        dy = -1;
        break;
      case "ArrowDown":
        dy = 1;
        break;
      case "+":
      case "=":
        // UX brief §6.2 — `+`/`=` resizes both axes by +1.
        dw = 1;
        dh = 1;
        break;
      case "-":
      case "_":
        // UX brief §6.2 — `-`/`_` resizes both axes by -1.
        dw = -1;
        dh = -1;
        break;
      default:
        return;
    }
    event.preventDefault();
    onKeyboardNudge(widgetKey, dx, dy, dw, dh);
  };

  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label={title}
      // Test selectors AC2 / AC3 / AC4 / AC6 / AC9 read these attrs to
      // verify grid positioning. RGL's CSS transform is not directly
      // assertable; the data-* mirror is. Mirrors the AnalysisWorkspace
      // pattern (see Story 010 acceptance suite).
      data-grid-x={layoutEntry?.x ?? null}
      data-grid-y={layoutEntry?.y ?? null}
      data-grid-w={layoutEntry?.w ?? null}
      data-grid-h={layoutEntry?.h ?? null}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: `${String(PANEL_RADIUS_PX)}px`,
      }}
      {...(style ? { style } : {})}
      {...(className ? { className } : {})}
      {...(onMouseDown ? { onMouseDown } : {})}
      {...(onMouseUp ? { onMouseUp } : {})}
      {...(onTouchEnd ? { onTouchEnd } : {})}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Tooltip title="Drag to move">
          <Box
            className={DRAG_HANDLE_CLASS}
            data-testid="widget-drag-handle"
            role="button"
            aria-label={`Move ${title}`}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              cursor: "move",
              color: "text.secondary",
              borderRadius: 0.5,
              outline: "none",
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
            }}
          >
            <GripVerticalIcon fontSize="small" />
          </Box>
        </Tooltip>
        <Typography
          component="h2"
          sx={{
            flexGrow: 1,
            fontSize: typography.size["label-md"],
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["label-md"],
            color: "text.primary",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Tooltip title={`Hide ${title}`}>
          <IconButton
            size="small"
            onClick={() => {
              onHide(widgetKey);
            }}
            aria-label={`Hide ${title}`}
            sx={{ width: 24, height: 24 }}
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>{children}</Box>
    </Paper>
  );
}
